/**
 * Indexable Sofia neighborhood landings for local SEO.
 * Slugs are Latin (site-wide URL convention); visible copy stays in the page locale.
 */

export const SOFIA_SERVICE_AREAS = [
  {
    slug: 'center',
    featured: true,
    serviceHref: '/services/residential',
    image: '/images/serviceRelated/pixabay-residential.webp',
  },
  {
    slug: 'lozenets',
    featured: true,
    serviceHref: '/services/regular-home-cleaning',
    image: '/images/serviceRelated/home.webp',
  },
  {
    slug: 'oborishte',
    featured: false,
    serviceHref: '/services/office-cleaning',
    image: '/images/serviceRelated/office.webp',
  },
  {
    slug: 'krasno-selo',
    featured: true,
    serviceHref: '/services/post-construction-cleaning',
    image: '/images/serviceRelated/specialized-post-construction.webp',
  },
  {
    slug: 'vitosha',
    featured: true,
    serviceHref: '/services/regular-home-cleaning',
    image: '/images/serviceRelated/livingroom.webp',
  },
  {
    slug: 'studentski-grad',
    featured: false,
    serviceHref: '/services/move-in-out-cleaning',
    image: '/images/serviceRelated/residential-move-in-out.webp',
  },
  {
    slug: 'lyulin',
    featured: false,
    serviceHref: '/services/residential',
    image: '/images/serviceRelated/homebackground.webp',
  },
  {
    slug: 'nadezhda',
    featured: true,
    serviceHref: '/services/commercial',
    image: '/images/serviceRelated/pixabay-commercial.webp',
  },
  {
    slug: 'mladost',
    featured: true,
    serviceHref: '/services/office-cleaning',
    image: '/images/serviceRelated/commercial-office.webp',
  },
  {
    slug: 'manastirski-livadi',
    featured: false,
    serviceHref: '/services/residential',
    image: '/images/serviceRelated/residential-spring-deep.webp',
  },
  {
    slug: 'borovo',
    featured: false,
    serviceHref: '/services/stairwell-cleaning',
    image: '/images/serviceRelated/commercial-stairwell.webp',
  },
  {
    slug: 'druzhba',
    featured: false,
    serviceHref: '/services/residential',
    image: '/images/serviceRelated/residential-holiday-apartment.webp',
  },
] as const;

/**
 * Every Sofia district landing shows the shared 80 m² home-cleaning price guide
 * (booking-form totals, district-named framing).
 */
export const HOME_GUIDE_DISTRICT_SLUGS = [
  'center',
  'lozenets',
  'oborishte',
  'krasno-selo',
  'vitosha',
  'studentski-grad',
  'lyulin',
  'nadezhda',
  'mladost',
  'manastirski-livadi',
  'borovo',
  'druzhba',
] as const satisfies ReadonlyArray<(typeof SOFIA_SERVICE_AREAS)[number]['slug']>;

export type HomeGuideDistrictSlug = (typeof HOME_GUIDE_DISTRICT_SLUGS)[number];

export function hasDistrictHomeGuide(slug: string): slug is HomeGuideDistrictSlug {
  return (HOME_GUIDE_DISTRICT_SLUGS as readonly string[]).includes(slug);
}

/** Money-page strips: 4–6 crawlable district URLs plus the hub “all neighborhoods” link. */
export const SERVICE_PAGE_NEIGHBORHOOD_SLUGS = {
  '/services/regular-home-cleaning': [
    'lozenets',
    'vitosha',
    'center',
    'krasno-selo',
    'lyulin',
    'manastirski-livadi',
  ],
  '/services/residential': [
    'center',
    'lyulin',
    'lozenets',
    'manastirski-livadi',
    'druzhba',
    'vitosha',
  ],
  '/services/office-cleaning': [
    'mladost',
    'oborishte',
    'nadezhda',
    'center',
    'lozenets',
    'lyulin',
  ],
  '/services/fitness-gym-cleaning': [
    'mladost',
    'studentski-grad',
    'lozenets',
    'center',
    'vitosha',
    'lyulin',
  ],
  '/services/commercial': [
    'nadezhda',
    'mladost',
    'oborishte',
    'center',
    'lyulin',
    'druzhba',
  ],
  '/services/move-in-out-cleaning': [
    'studentski-grad',
    'lozenets',
    'center',
    'mladost',
    'lyulin',
    'vitosha',
  ],
  '/services/post-construction-cleaning': [
    'krasno-selo',
    'center',
    'lozenets',
    'vitosha',
    'manastirski-livadi',
    'lyulin',
  ],
  '/services/window-cleaning': [
    'mladost',
    'oborishte',
    'lozenets',
    'center',
    'vitosha',
    'manastirski-livadi',
  ],
  '/services/stairwell-cleaning': [
    'borovo',
    'lyulin',
    'krasno-selo',
    'druzhba',
    'center',
    'nadezhda',
  ],
  '/pricing': ['center', 'mladost', 'lyulin', 'studentski-grad', 'nadezhda', 'vitosha'],
} as const satisfies Record<string, ReadonlyArray<(typeof SOFIA_SERVICE_AREAS)[number]['slug']>>;

export type SofiaServicePagePath = keyof typeof SERVICE_PAGE_NEIGHBORHOOD_SLUGS;

export type SofiaServiceArea = (typeof SOFIA_SERVICE_AREAS)[number];
export type SofiaServiceAreaSlug = SofiaServiceArea['slug'];

const SLUG_SET = new Set<string>(SOFIA_SERVICE_AREAS.map(area => area.slug));
const SERVICE_PAGE_PATH_SET = new Set<string>(Object.keys(SERVICE_PAGE_NEIGHBORHOOD_SLUGS));

export function isSofiaServiceAreaSlug(value: string): value is SofiaServiceAreaSlug {
  return SLUG_SET.has(value);
}

export function isSofiaServicePagePath(value: string): value is SofiaServicePagePath {
  return SERVICE_PAGE_PATH_SET.has(value);
}

/** Resolve `/services/{slug}` to a money-page strip path when one is configured. */
export function servicePagePathForSlug(slug: string): SofiaServicePagePath | null {
  const path = `/services/${slug}`;
  return isSofiaServicePagePath(path) ? path : null;
}

export function getSofiaServiceArea(slug: string): SofiaServiceArea | undefined {
  return SOFIA_SERVICE_AREAS.find(area => area.slug === slug);
}

export function sofiaServiceAreaPath(
  slug: SofiaServiceAreaSlug,
): `/service-areas/${SofiaServiceAreaSlug}` {
  return `/service-areas/${slug}`;
}

export function listSofiaServiceAreaSlugs(): SofiaServiceAreaSlug[] {
  return SOFIA_SERVICE_AREAS.map(area => area.slug);
}

export function neighborhoodSlugsForServicePage(
  servicePath: SofiaServicePagePath,
): SofiaServiceAreaSlug[] {
  return [...SERVICE_PAGE_NEIGHBORHOOD_SLUGS[servicePath]];
}

export function listFooterNeighborhoodSlugs(): SofiaServiceAreaSlug[] {
  return SOFIA_SERVICE_AREAS.filter(area => area.featured).map(area => area.slug);
}
